import { useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { TransactionDocuments } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { setPanelIsOpen } from '../store/slices/incoming-transaction';
import {
  useUploadDocumentMutation,
  useLazyGetProblemTransactionDocumentFileQuery,
  useAddDocumentToProblemTransactionMutation,
  useLazyGetProblemTransactionsDocumentsQuery,
} from '../store/services/customers';
import { UploadResponse } from '../types';

const InternalTransactionDocuments = () => {
  const [uploadedFilesError, setUploadedFilesError] = useState<string[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState('');

  const {
    customersProblemTransactions: {
      internal: { transaction, documents, documentsPagination },
    },
    customersDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const { transactionId } = useParams();

  const [getTransactionDocuments, { isFetching: documentsIsFetching }] =
    useLazyGetProblemTransactionsDocumentsQuery();

  const [upload, { isLoading: fileUploadInProgress }] =
    useUploadDocumentMutation();
  const [
    getTransactionDocumentFile,
    {
      isLoading: downloadInProgress,
      isFetching: downloadIsFetching,
      isSuccess: downloadIsSuccess,
    },
  ] = useLazyGetProblemTransactionDocumentFileQuery();

  const [
    addDocumentsToTransaction,
    { isLoading: addDocumentInProgress, isSuccess: fileUploadIsSuccess },
  ] = useAddDocumentToProblemTransactionMutation();

  const {
    setSubPanelVisible,
    setSelectedDocumentName,
    getTransactionDocument,
  } = useOutletContext<{
    setSubPanelVisible: (data: boolean) => void;
    setSelectedDocumentName: (data: string) => void;
    getTransactionDocument: (data: string) => void;
  }>();

  const getTransationDocumentsAction = (cursor?: string) => {
    const body: {
      id: string;
      cursor?: string;
    } = {
      id: transactionId as string,
    };

    if (cursor) {
      body.cursor = cursor;
    }

    getTransactionDocuments(body);
  };

  const onDownloadDocument = async (id: string) => {
    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    const currentDocument = documents?.find((item) => item.id === id);

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${currentDocument?.name}`;
      hiddenElement.click();
    }
  };

  const setTransactionSidepanelVisible = (data: boolean) => {
    dispatch(setPanelIsOpen(data));
  };

  const onUploadDocument = async (files: FileList, organizationId?: string) => {
    setUploadedFilesError([]);
    const nextUploadedFilesErrors: string[] = [];

    const filesResponse = await Promise.all(
      Object.keys(files).map(async (file) => {
        const fileItem = files[file as any];

        const response = (await upload(files[file as any])) as unknown as {
          data?: UploadResponse[];
          error?: {
            originalStatus: number;
            data?: {
              message?: string;
            };
          };
          message?: string;
        };

        let nextFile;

        if (response && response.data) {
          nextFile = { ...response.data[0] };

          let fileName = fileItem?.name;

          if (
            response.data[0] &&
            response.data[0]?.mimeType?.includes('image')
          ) {
            fileName = `${fileItem?.name.slice(0, fileItem?.name.lastIndexOf('.'))}.jpg`;
          }

          nextFile.name = fileName;
        } else if (response.error) {
          if (response.error.originalStatus === 413) {
            nextUploadedFilesErrors.push(
              `${files[file as any].name} size too large`,
            );
          } else {
            // eslint-disable-next-line no-lonely-if
            if (response?.error?.data?.message) {
              nextUploadedFilesErrors.push(response?.error?.data?.message);
            }
          }
        }

        return nextFile;
      }),
    );

    setUploadedFilesError(nextUploadedFilesErrors);

    if (filesResponse && filesResponse.length) {
      const uploadedFiles: { name: string; uploadId: string }[] = [];

      filesResponse.forEach((item) => {
        if (item && item.name && item.id) {
          uploadedFiles.push({
            name: item?.name,
            uploadId: item?.id,
          });
        }
      });

      if (uploadedFiles.length) {
        await Promise.all(
          uploadedFiles.map(async (item) => {
            const body: {
              uploadId: string;
              name: string;
              organizationId: string;
            } = {
              uploadId: item.uploadId,
              name: item.name,
              organizationId: organizationId as string,
            };

            const response = await addDocumentsToTransaction({
              id: transactionId as string,
              body,
            });

            return response;
          }),
        );
      }
    }
  };

  useEffect(() => {
    if (downloadIsSuccess && selectedDocumentId && !downloadInProgress) {
      setSelectedDocumentId('');
    }
  }, [downloadIsSuccess, selectedDocumentId, downloadInProgress]);

  useEffect(
    () => () => {
      setSelectedDocumentId('');
      setUploadedFilesError([]);
    },
    [],
  );

  return (
    <TransactionDocuments
      documents={documents}
      setSubPanelVisible={setSubPanelVisible}
      setSelectedDocumentName={setSelectedDocumentName}
      setTransactionSidepanelVisible={setTransactionSidepanelVisible}
      onUploadDocument={onUploadDocument}
      uploadDocumentInProgress={fileUploadInProgress || addDocumentInProgress}
      uploadDocumentIsSuccess={fileUploadIsSuccess}
      uploadDocumentErrors={uploadedFilesError}
      selectedDocumentId={selectedDocumentId}
      setSelectedDocumentId={setSelectedDocumentId}
      downloadInProgress={downloadInProgress || downloadIsFetching}
      senderAccount={transaction?.senderAccount}
      recipientAccount={transaction?.recipientAccount}
      onDownloadDocument={onDownloadDocument}
      activePermission={activePermissions}
      getTransactionDocument={getTransactionDocument}
      documentsPagination={documentsPagination}
      documentsIsFetching={documentsIsFetching}
      getTransactionDocuments={getTransationDocumentsAction}
      internal
    />
  );
};

export default InternalTransactionDocuments;
